/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {useEffect, useMemo, useRef, useState} from 'react'
import '../../../../camera/CameraPerformance.css'
import {BottomButtonsWrapper} from '../../../../camera/SensorCamera/ItemDetails/DetectionCapability/styled';
import ArgosButton from "../../../../../components/Buttons/ArgosButton";
import {useAppSelector} from "../../../../../../redux/hooks";
import ArgosTextField from "../../../../../components/Form/TextField/ArgosTextField";
import ArgosFlexbox from "../../../../../components/Containers/ArgosFlexbox";
import {selectGlobalDialogState} from '../../../../../../redux/ducks/GlobalDialog/slice';
import {useHasWriteAccess} from "../../../../../../redux/ducks/AccessControl/accessControlHelpers";

import {Checkbox, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControlLabel,} from '@mui/material';
import {usePostRequest} from "../../../../../../api/helper";
import RightPanel from './CollapsibleTabs/RightPanel';
import ObjectProperties, {groupObjectsByTypeNameVersionProfile} from './CollapsibleTabs/ObjectProperties';
import RadarProperties from './CollapsibleTabs/RadarProperties';
import ArgosCollapseComponent from '../../../../../components/CollapseComponent/ArgosCollapseComponent';
import {theme} from '../../../../../../constants/theme';
import {getRadarPlotEndpoint} from '../../constants';
import {toast} from 'react-hot-toast';
import {COLUMN_NAMES, MAP_OBJECT_PROFILE_NAME_TO_KEY} from "../constants";
import VAR_COMMON_CONSTANTS from "../../../../../../constants/globalVariables";
import {cloneDeep} from "lodash";
import {StringParam, useQueryParam} from "use-query-params";

/**
 * This function is triggered when the user selects a new category, object name, and version.
 *
 * @param category - The category of the object (e.g., "Baseline", "Potentially Moving Object").
 * @param name - The name of the object.
 * @param version - The version number of the object.
 * @param objectVariant - The variant of the object (e.g., "Front Profile", "Side Profile").
 * @param objectData - An array of objects containing data about the available objects.
 * @returns {null | number | undefined} - The radar cross-section (RCS) value if found (number), null if no object matches, or undefined if the object variant does not exist.
 */
export const getRcs = (category: string, name: string, version: number, objectVariant: string, objectData: any[]) => {
  const filtered = objectData.filter((item: any) =>
    item.type === category &&
    item.objectName === name &&
    item.latestVersion === version
  );
  if (filtered.length > 0) {
    const rcs = filtered[0]?.parameters?.[MAP_OBJECT_PROFILE_NAME_TO_KEY[objectVariant]]?.radarCrossSection;
    return rcs
  } else {
    return null
  }
}

const Content = (props: any) => {

  const buildPlotData = (_data: any) => {
    const data: any = {x: _data?.x, y: _data?.y}
    const columns = {xLabel: COLUMN_NAMES.AZIMUTH_ANGLE, yLabel: COLUMN_NAMES.DISTANCE}
    const returnObj = {data, columns}
    return returnObj
  }

  const hasWriteAccess = useHasWriteAccess()
  const [baselineData, setBaselineData] = useState(props.baselineData)
  const [plotData, setPlotData] = useState(buildPlotData(props.sensorRadarData.plotData[Object.keys(props.sensorRadarData.plotData)[0]]?.data))
  const [fovAndBoresightRadarValues, setFovandBoresightRadarValues] = useState<any>({});
  const [objectData, setObjectData] = useState(props.objectData);
  const [objectDataMap, setObjectDataMap] = useState(props.objectDataMap);
  const [configName, setConfigName] = useState("");
  const [openSavePopup, setOpenSavePopup] = useState(false);
  const [openDeletePopup, setOpenDeletePopup] = useState(false);
  const [isLoadingPlot, setIsLoadingPlot] = useState(false);
  const gmid = props.gmid
  const isDialogOpen = useAppSelector(selectGlobalDialogState).isOpen
  const [isError, setIsError] = useState(false);
  const [componentDataMap, setComponentDataMap] = useState<any>({});
  const [temperatureArray, setTemperatureArray] = useState<number[]>([]);

  const [sensorId, setSensorId] = useQueryParam('id', StringParam)

  useEffect(() => {
    setConfigName(baselineData['configName'])
  }, [baselineData?.['configName']]);

  useEffect(() => {
    setBaselineData(props.baselineData);
    setObjectData(props.objectData);
    setObjectDataMap(props.objectDataMap);
    let _plotData = props.sensorRadarData?.plotData[getPlotDataName(props.baselineData.temperature)]?.data
    setPlotData(buildPlotData(_plotData))
    getTemperatureArray();
  }, [props])


  useEffect(() => {
    handleUpdatePlot(props.baselineData)
  }, [props.configIndex, sensorId, props.baselineData])


  function handleTempChange(newTempValue: string | unknown) {
    let matchingSnrValue = 0;
    let matchingRcsValue = 0;

    let newObject = {
      ...baselineData,
      temperature: newTempValue,
    }

    const isBaseline = baselineData.objectCategory === 'Baseline';

    // Only set the snr and rcs to defaults if the current selected object type is Baseline
    if (isBaseline) {
      // Get default values for SNR and RCS based on the selected temperature
      for (const key of Object.keys(props.sensorRadarData.plotData ?? {})) {
        const data = props.sensorRadarData.plotData[key]?.data;
        if (!data) {
          continue;
        }
        const tempKey = Object.keys(data).find(k => k.includes('temperature') && k.includes('_value'));
        const snrKey = Object.keys(data).find(k => k.includes('snr') && k.includes('_value'));
        const rcsKey = Object.keys(data).find(k => k.includes('rcs') && k.includes('_value'));
        if (!tempKey || !snrKey || !rcsKey) {
          continue;
        }
        const tempValue = data?.[tempKey];
        if (tempValue === newTempValue) {
          matchingSnrValue = data?.[snrKey];
          matchingRcsValue = data?.[rcsKey];
          break; // Exit the loop once a match is found
        }
      }

      newObject = {
        ...newObject,
        snr: matchingSnrValue,
        rcs: matchingRcsValue,
      };
    }

    setBaselineData(newObject);
  }

  const getTemperatureArray = () => {
    const sensorData = props.sensorRadarData.plotData
    let _temperatureArray: number[] = [];
    Object.keys(sensorData)?.forEach((key: string) => {
      if (key != "instrumentationLimits") {
        const detectiondata = sensorData[key].data
        const tempValue = Object.keys(detectiondata).filter((keys: string) => keys.includes("temperature") && keys.includes("value"))[0] as unknown as number
        _temperatureArray.push(detectiondata[tempValue])
      }
    })
    setTemperatureArray(_temperatureArray)
  }

  function getBaselineObject() {
    const baselineObjects = objectData.filter((item: any) => (item.type == "Baseline"))
    const latestVersion = baselineObjects.reduce(function (prev: any, current: any) {
      return (prev.version > current.version) ? prev : current
    })

    const baselineObject = {
      "version": latestVersion.version,
      "id": latestVersion.id,
      "links": latestVersion.links,
      "pointsOnObject": latestVersion.pointsOnObject,
      "pixelsAcrossShortDimension": latestVersion.pixelsAcrossShortDimension,
      "rcs": latestVersion.rcs,
      "heightVertical": latestVersion.heightVertical,
      "widthHorizontal": latestVersion.widthHorizontal,
      "type": "Baseline",
      "name": latestVersion.name,
      "globalId": latestVersion.globalId,
      objectVariant: latestVersion.objectVariant,
    }
    return baselineObject
  }

  function getLatestVersion(category: any, name: any) {
    const allVersions = objectData.filter((item: any) => (item.type == category && item.objectName == name))
    if (!allVersions || allVersions.length === 0) {
      return null
    }
    const latestVersion = allVersions.reduce(function (prev: any, current: any) {
      return (prev.latestVersion > current.latestVersion) ? prev : current
    })
    return latestVersion
  }

  function handleObjectCategoryChange(e: any) {
    // this just constructs the baseline object from sensor and rwo. It is being pulled from database 
    const baselineObject = getBaselineObject()
    let newData = {}
    let isBaseline: boolean = e.target.value == "Baseline"
    if (e.target.value == "Baseline") {
      newData = {
        ...baselineData,
        "objectCategory": e.target.value,
        "objectName": isBaseline ? baselineObject.name : "",
        "objectVersion": isBaseline ? baselineObject.version : "",
        objectVariant: isBaseline ? baselineObject.objectVariant : "",
        "rcs": isBaseline ? baselineObject.rcs : "",
      }
    } else if (e.target.value === "Potentially Moving Object" || e.target.value === "Miscellaneous") {
      newData = {
        ...baselineData,
        "objectCategory": e.target.value,
        "objectName": isBaseline ? baselineObject.name : "",
        "objectVersion": isBaseline ? baselineObject.version : "",
        "rcs": isBaseline ? baselineObject.rcs : "",
      }
    }
    setBaselineData(newData)
  }

  function handleObjectNameChange(e: any) {
    const latestVersion = getLatestVersion(baselineData.objectCategory, e.target.value)
    if (!latestVersion) {
      return
    }
    const objectVariant = MAP_OBJECT_PROFILE_NAME_TO_KEY[baselineData.objectVariant]
    const rcs = latestVersion.parameters[objectVariant]?.radarCrossSection || null
    const newData = {
      ...baselineData,
      "objectName": e.target.value,
      "objectVersion": latestVersion.version,
      "rcs": rcs,
    }
    setBaselineData(newData)
  }
  /**
   * Update the object version and rcs based on the selected category, object name and version.
   * This function is triggered when the user selects a new version from the dropdown.
   * 
   * @param e - The event object triggered by the version change.
   * @returns  {void} - Updates the baseline data with the new version and RCS properties.
   */
  function handleObjectVersionChange(e: any) {
    const version = Number(e.target.value);
    if (isNaN(version)) {
      console.error("Invalid version: must be a number");
      return;
    }
    const newData = {
      ...baselineData,
      "objectVersion": version,
      "rcs": getRcs(baselineData.objectCategory, baselineData.objectName, version, baselineData.objectVariant, objectData),
    }
    setBaselineData(newData);
  }

  /**
   * Update the object id based on the selected object.
   */
  const groupedObjects = useMemo(() => groupObjectsByTypeNameVersionProfile(props.objectData), [props.objectData])
  const refGroupedObjects = useRef<any>(groupedObjects)
  useEffect(() => {
    refGroupedObjects.current = groupedObjects
  }, [groupedObjects])
  useEffect(() => {
    const updatedBaselineData = addObjectIdToBaselineData(baselineData, refGroupedObjects)
    setBaselineData(updatedBaselineData)
  }, [baselineData.objectName, baselineData.objectVersion, baselineData.objectVariant, groupedObjects]);

  /**
   * Updates the baseline data based on the selected profile and its corresponding
   * radar cross-section (RCS) value.
   *
   * @param {Event} e - The event object triggered by the profile change.
   * @param {any} e.target.value - The selected profile value (e.g., "Front Profile").
   *
   * @returns {void} - Updates the baseline data with the new profile and RCS properties.
   *
   * - Depending on the `target.value`, retrieves the RCS value from `baselineData`
   *   for the "Front Profile," "Side Profile," or "Rear Profile." If no matching
   *   case is found, a default RCS value from `baselineData` is used.
   *
   * - Constructs a `newData` object containing the updated `objectProperties` and
   *   all relevant RCS values from `baselineData` (e.g., `rcsFront`, `rcsSide`, `rcsRear`).
   *
   * - Calls `setBaselineData` to persist the updated `newData`.
   */
  function handleProfileChange(e: any) {
    const newData = {
      ...baselineData,
      "objectVariant": e.target.value,
      "rcs": getRcs(baselineData.objectCategory, baselineData.objectName, baselineData.objectVersion, e.target.value, objectData),
    }
    setBaselineData(newData)
  }


  function getRCSDBSM(category: any, name: any, version: any) {

    const filtered = objectData.filter((item: any) =>
      item.type === category &&
      item.objectName === name &&
      item.latestVersion === version
    );

    if (filtered.length > 0) {
      const rcs = filtered[0]?.parameters?.frontProfile?.radarCrossSection;
      return rcs
    }
  }

  function setRCS(newRCSValue: string) {
    if (baselineData.objectCategory == "Custom") {
      const newObject = {
        ...baselineData,
        'rcs': newRCSValue
      }
      setBaselineData(newObject);
    }

  }

  function setSNR(newSNRValue: string) {
    const newObject = {
      ...baselineData,
      'snr': newSNRValue
    }
    setBaselineData(newObject);
  }

  function setAttenuation(newAttenuationValue: string) {
    const newObject = {
      ...baselineData,
      'attenuation': newAttenuationValue
    }
    setBaselineData(newObject);
  }

  const url = getRadarPlotEndpoint
  const getRadarPlotHandle = usePostRequest(url)

  async function handleUpdatePlot(_baselineData: any) {
    setIsLoadingPlot(true)
    let newPlotData = {
      "rcs": _baselineData.rcs,
      "attenuation": _baselineData.attenuation,
      "snr": _baselineData.snr,
      "objectId": _baselineData.objectId,
      'instrumentationRange': props.sensorRadarData.maxInstrumentedRange,
      'instrumentationAngle': props.sensorRadarData.azimuthFOV,
      sensorId,
      temperature: _baselineData.temperature,

      "plotData": props.sensorRadarData.plotData[getPlotDataName(_baselineData.temperature)]?.data,
    }
    getRadarPlotHandle(newPlotData).then((responseJson) => {
      setIsError(false)
      const fovAndBoresightRadarObject = {
        'azimuth_fov': responseJson["azimuth_fov"],
        'elevation_fov': responseJson["elevation_fov"],
        'max_range': responseJson["max_range"]
      }
      const data = {
        'x': responseJson['x'],
        'y': responseJson['y']
      }
      setPlotData(buildPlotData(data))
      setFovandBoresightRadarValues(fovAndBoresightRadarObject)
      setIsLoadingPlot(false);
    }).catch(err => {
      toast.error("Error while updating plot. Please check your values and try again")
      setIsError(true)
      setIsLoadingPlot(false)
    })
  }

  function handleReset() {
    setBaselineData(props.baselineData)
  }

  function getPlotDataName(_tempValue: string) {
    const sensorData = props.sensorRadarData.plotData
    let detectionDataKey: any = ""
    Object.keys(sensorData)?.forEach((key: string) => {
      if (key != "instrumentationLimits") {
        const detectiondata = sensorData[key].data
        const tempValue = Object.keys(detectiondata).filter((keys: string) => keys.includes("temperature") && keys.includes("value"))[0]
        if (detectiondata[tempValue] == _tempValue) {
          detectionDataKey = key
        }
      }

    })
    return detectionDataKey
  }

  function onSaveConfiguration() {
    props.onSaveConfiguration(null, baselineData, configName, gmid)
  }

  function onOverrideConfiguration() {
    props.onSaveConfiguration(props.configIndex - 1, baselineData, configName, gmid)
  }

  const handleClose = () => {
    setOpenSavePopup(false);
    setOpenDeletePopup(false);
  };

  const handleClickSaveOpen = () => {
    setOpenSavePopup(true);
  };

  function handleSaveConfigName(e: any) {
    setConfigName(e.target.value)
  }

  function onDeleteConfiguration() {
    props.onDeleteConfiguration(props.baselineData.configName, props.baselineData.owner)
  }

  function processListOfVersions(listOfElements: any) {
    let local_map: any = {}
    listOfElements.forEach((item: any) => {
      if (local_map[item.name] !== undefined) {
        local_map[item.name].push(item.version)
      } else {
        local_map[item.name] = []
        local_map[item.name].push(item.version)
      }
    })
    return local_map
  }

  function setIsPublicConfig(_isPublicConfig: any) {
    const newObject = {
      ...baselineData,
      'isPublicConfig': _isPublicConfig
    }
    setBaselineData(newObject);
  }

  useEffect(() => {
    createCollapsibleTabs()
  }, [props, baselineData, objectDataMap, plotData, openSavePopup, configName, sensorId])

  const createCollapsibleTabs = () => {
    let _componentDataMap: any = {}
    _componentDataMap["Radar Properties"] = <RadarProperties
      temperatureArray={temperatureArray}
      sensorRadarData={props?.sensorRadarData}
      isDialogOpen={isDialogOpen}
      handleTempChange={handleTempChange}
      baselineData={baselineData}
    />
    _componentDataMap[VAR_COMMON_CONSTANTS.sensorApp.all.SENSOR_TESTING_CONDITIONS_HEADER] = <ObjectProperties
      temperatureArray={temperatureArray}
      handleTempChange={handleTempChange}
      baselineData={baselineData}
      isDialogOpen={isDialogOpen}
      handleObjectCategoryChange={handleObjectCategoryChange}
      objectData={objectData}
      objectDataMap={objectDataMap}
      handleObjectNameChange={handleObjectNameChange}
      processListOfVersions={processListOfVersions}
      handleObjectVersionChange={handleObjectVersionChange}
      handleProfileChange={handleProfileChange}
      gmid={gmid}
      configIndex={gmid}
      setRCS={setRCS}
      setSNR={setSNR}
      setAttenuation={setAttenuation}
      setIsPublicConfig={setIsPublicConfig}
    />
    _componentDataMap["Sensor Performance"] = <RightPanel
      isLoadingPlot={isLoadingPlot}
      isError={isError}
      plotData={plotData}
      handleUpdatePlot={handleUpdatePlot}
      baselineData={baselineData}
      handleReset={handleReset}
      handleClickSaveOpen={handleClickSaveOpen}
      openSavePopup={openSavePopup}
      handleClose={handleClose}
      handleSaveConfigName={handleSaveConfigName}
      configName={configName}
      configIndex={props?.configIndex}
      gmid={gmid}
      onOverrideConfiguration={onOverrideConfiguration}
      onSaveConfiguration={onSaveConfiguration}
      onDeleteConfiguration={onDeleteConfiguration}
      fovAndBoresightRadarValues={fovAndBoresightRadarValues}
    />
    setComponentDataMap(_componentDataMap)
  }

  return (
    <ArgosFlexbox vertical style={{overflow: 'auto', height: '100%', width: '100%'}}>
      {/* Make it own component */}
      <ArgosFlexbox style={{alignContent: 'center'}}>
        <BottomButtonsWrapper style={{marginTop: '0px'}}>
          <ArgosButton disabled={isLoadingPlot} onClick={() => handleUpdatePlot(baselineData)}>Update Plot</ArgosButton>
          <ArgosButton onClick={handleReset}>Reset Defaults</ArgosButton>
          {hasWriteAccess && <ArgosButton
            onClick={handleClickSaveOpen}
          >
            Save Configuration
          </ArgosButton>}
          <Dialog
            open={openSavePopup}
            onClose={handleClose}
          >
            <DialogTitle>
              Save Configuration
            </DialogTitle>
            <DialogContent>
              <DialogContentText>
                What would you like to name this configuration?
              </DialogContentText>
              <ArgosTextField
                autoFocus
                margin="dense"
                id="name"
                fullWidth
                variant="standard"
                onChange={handleSaveConfigName} value={configName}
              />
            </DialogContent>
            <DialogActions>
              <ArgosButton autoFocus onClick={handleClose}>Cancel</ArgosButton>
              <ArgosButton
                disabled={props?.configIndex == 0 || baselineData.owner != props.gmid}
                onClick={function (event) {
                  onOverrideConfiguration();
                  handleClose()
                }}
              >
                Override Configuration
              </ArgosButton>
              <ArgosButton
                onClick={function (event) {
                  onSaveConfiguration();
                  handleClose()
                }}
              >
                Save as New Configuration
              </ArgosButton>
            </DialogActions>
          </Dialog>
          {hasWriteAccess &&
            <ArgosButton
              disabled={props.configIndex == 0 || baselineData.owner != props.gmid}
              onClick={onDeleteConfiguration}
            >
              Delete Configuration
            </ArgosButton>
          }
        </BottomButtonsWrapper>
        <FormControlLabel
          control={<Checkbox
            onChange={(e) => {
              setIsPublicConfig(e.target.checked);
            }}
            checked={baselineData?.isPublicConfig} name="isPublicConfig"
            disabled={baselineData.owner != props?.gmid || props.configIndex == 0}
          />} label="Public Configurations"/>
      </ArgosFlexbox>
      <div style={{flex: 1, overflow: 'auto'}}>
        <ArgosCollapseComponent
          styleProps={{
            outlineOnly: true,
            fontColor: theme.layout.sidebar.backgroundColor,
          }}
          defaultOpen={true}
          data={componentDataMap ?? {}}
        />
      </div>

    </ArgosFlexbox>
  )
}
export default Content

export const addObjectIdToBaselineData = (baselineData: any, refGroupedObjects: any) => {
  const clonedBaselineData = cloneDeep(baselineData)
  const objectType = clonedBaselineData.objectCategory
  const objectName = clonedBaselineData.objectName
  const objectVersion = clonedBaselineData.objectVersion
  const objectVariant = MAP_OBJECT_PROFILE_NAME_TO_KEY[clonedBaselineData.objectVariant]
  const val = refGroupedObjects.current?.[objectType]?.[objectName]?.[objectVersion]?.[objectVariant]
  if (objectType !== 'Baseline' && val) {
    const objectVariantVal = refGroupedObjects.current[objectType][objectName][objectVersion][objectVariant]
    const objectId = objectVariantVal.id
    clonedBaselineData.objectId = objectId
  }
  return clonedBaselineData
}